/**
 * Alerts center — view all platform alerts and notify pumps via email/SMS.
 */
import { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import swal from 'sweetalert';
import Sidebar_Progress from '../../layouts/Admin_Sidebar_Progress.component';
import LoginHeaderOperational from '../LoginHeaderOperational';
import { API_URL } from '../../../constant/API_Settings';
import { fetchPlatformSuperAdminProfile } from '../../../utils/platformAuth';
import { adminNotifyPump, fetchAdminAlerts, fetchNotificationHistory } from './adminApi';
import './admin-dashboard.css';

const AdminAlertsCenter = (props) => {
  const history = useHistory();
  const [alerts, setAlerts] = useState([]);
  const [historyRows, setHistoryRows] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [notifyMsg, setNotifyMsg] = useState('');
  const [selectedPump, setSelectedPump] = useState(null);
  const [channels, setChannels] = useState({ email: true, sms: false });

  useEffect(() => {
    fetchPlatformSuperAdminProfile(API_URL, localStorage.getItem('AUTH_TOKEN'))
      .then((profile) => (profile ? load() : history.goBack()))
      .catch(() => history.goBack());
  }, [history]);

  function load() {
    setLoading(true);
    Promise.all([fetchAdminAlerts(null), fetchNotificationHistory()])
      .then(([alertData, histData]) => {
        setAlerts(alertData.alerts || []);
        setHistoryRows(histData.data || []);
      })
      .finally(() => setLoading(false));
  }

  const filtered = alerts.filter((a) => filter === 'all' || a.severity === filter);

  function openNotify(alert) {
    setSelectedPump(alert.pump_id);
    setNotifyMsg(`Petrosmart Admin Alert\n\n${alert.title}\n${alert.message}\n\nPlease review and correct your entries.`);
  }

  function sendNotify() {
    if (!selectedPump || notifyMsg.length < 10) {
      swal('Error', 'Enter a message (min 10 characters)', 'warning');
      return;
    }
    const ch = [];
    if (channels.email) ch.push('email');
    if (channels.sms) ch.push('sms');
    if (!ch.length) {
      swal('Error', 'Select at least one channel', 'warning');
      return;
    }
    adminNotifyPump(selectedPump, notifyMsg, ch, 'manual_alert')
      .then((res) => {
        swal('Sent', res.message || 'Notification sent', 'success');
        setSelectedPump(null);
        load();
      })
      .catch((err) => swal('Error', err.message, 'error'));
  }

  return (
    <div className="main-container page-wrap admin-platform">
      <div className="d-md-flex">
        <Sidebar_Progress props={props} progress={25} />
        <div className="content-area">
          <LoginHeaderOperational props={props} page_title="Alerts & Notifications" />
          <div className="container pb-3 mt-3">
            <Link to="/admin_dashboard" className="small">← Control Center</Link>

            <div className="admin-panel mt-3">
              <div className="d-flex gap-2 mb-3 flex-wrap">
                {['all', 'critical', 'warning', 'info'].map((f) => (
                  <button key={f} className={`btn btn-sm ${filter === f ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setFilter(f)}>
                    {f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
              </div>

              {loading ? <p>Loading alerts...</p> : (
                filtered.length === 0 ? <p className="text-muted">No alerts in this category.</p> :
                filtered.map((alert, idx) => (
                  <div key={idx} className={`admin-alert-item ${alert.severity}`}>
                    <div className="d-flex justify-content-between flex-wrap gap-2">
                      <div>
                        <strong>{alert.title}</strong>
                        <span className="admin-badge inactive ms-2">{alert.type}</span>
                        <div className="small mt-1">{alert.message}</div>
                        <div className="small text-muted">{alert.pump_name}</div>
                      </div>
                      <div className="admin-action-row">
                        <Link to={`/admin-pump/${alert.pump_id}`} className="btn btn-sm btn-outline-primary">Open pump</Link>
                        <button className="btn btn-sm btn-warning" onClick={() => openNotify(alert)}>Notify pump</button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {selectedPump && (
              <div className="admin-panel">
                <h5>Send notification to pump #{selectedPump}</h5>
                <textarea className="form-control mb-2" rows={5} value={notifyMsg} onChange={(e) => setNotifyMsg(e.target.value)} />
                <div className="mb-2">
                  <label className="me-3"><input type="checkbox" checked={channels.email} onChange={(e) => setChannels({ ...channels, email: e.target.checked })} /> Email</label>
                  <label><input type="checkbox" checked={channels.sms} onChange={(e) => setChannels({ ...channels, sms: e.target.checked })} /> SMS</label>
                </div>
                <button className="btn btn-primary me-2" onClick={sendNotify}>Send</button>
                <button className="btn btn-outline-secondary" onClick={() => setSelectedPump(null)}>Cancel</button>
              </div>
            )}

            <div className="admin-panel">
              <h5>Notification history</h5>
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead><tr><th>Date</th><th>Pump</th><th>Channel</th><th>Status</th><th>Message</th></tr></thead>
                  <tbody>
                    {historyRows.map((row) => (
                      <tr key={row.id}>
                        <td>{row.created_at?.slice(0, 16)}</td>
                        <td>{row.pump?.pump_name || row.pump_id}</td>
                        <td>{row.channel}</td>
                        <td>{row.status}</td>
                        <td className="small">{row.message?.slice(0, 80)}...</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAlertsCenter;
